import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface VirtualRoomFilterEntry {
  hasPassword: boolean
  hasMeetingLock: boolean
  meetingTitle?: string
  meetingCreatorName?: string
  ownerSessionId: string
  authorizedSessionIds: string[]
  isReception?: boolean
}

export interface CurrentVirtualRoom {
  roomId: string
  roomName: string
  /** The VR's kind (SC-422); a cubicle gets no room controls. */
  kind?: string
}

export interface KnockRequest {
  roomId: string
  knockerSessionId: string
  knockerName: string
  reason?: 'password' | 'meeting'
}

export interface VirtualRoomFilterState {
  filters: Record<string, VirtualRoomFilterEntry>
  lastError: string | null
  currentVirtualRoom: CurrentVirtualRoom | null
  knockRequest: KnockRequest | null
  knockDenied: boolean
}

const initialState: VirtualRoomFilterState = {
  filters: {},
  lastError: null,
  currentVirtualRoom: null,
  knockRequest: null,
  knockDenied: false,
}

const virtualRoomFilterSlice = createSlice({
  name: 'virtualRoomFilter',
  initialState,
  reducers: {
    setFilterState: (state, action: PayloadAction<Record<string, VirtualRoomFilterEntry>>) => {
      state.filters = action.payload
      state.lastError = null
    },
    patchRoomOwners: (state, action: PayloadAction<Record<string, string>>) => {
      for (const [roomId, ownerSessionId] of Object.entries(action.payload)) {
        if (!ownerSessionId) continue
        const existing = state.filters[roomId]
        // Only patch the owner of a room we already hold an authoritative filter entry for.
        // Never fabricate a `hasPassword: false` entry here: a locked room whose VIRTUAL_ROOM_FILTER_STATE
        // hasn't arrived yet would otherwise read as OPEN and make the password gate fail open — the
        // asymmetric-media bug after a leave→rejoin. Ownership crown for an unknown room waits for
        // the authoritative filter-state. [SC-246]
        if (existing) {
          existing.ownerSessionId = ownerSessionId
        }
      }
      state.lastError = null
    },
    setFilterError: (state, action: PayloadAction<string | null>) => {
      state.lastError = action.payload
    },
    setCurrentVirtualRoom: (state, action: PayloadAction<CurrentVirtualRoom | null>) => {
      state.currentVirtualRoom = action.payload
    },
    setKnockRequest: (state, action: PayloadAction<KnockRequest | null>) => {
      state.knockRequest = action.payload
    },
    clearKnockRequest: (state) => {
      state.knockRequest = null
    },
    setKnockDenied: (state, action: PayloadAction<boolean>) => {
      state.knockDenied = action.payload
    },
    resetState: () => initialState,
  },
})

export const { setFilterState, patchRoomOwners, setFilterError, setCurrentVirtualRoom, setKnockRequest, clearKnockRequest, setKnockDenied, resetState } = virtualRoomFilterSlice.actions

export default virtualRoomFilterSlice.reducer

// Selectors
export const selectVirtualRoomFilters = (state: { virtualRoomFilter: VirtualRoomFilterState }) =>
  state.virtualRoomFilter.filters

export const selectRoomFilter = (state: { virtualRoomFilter: VirtualRoomFilterState }, roomId: string) =>
  state.virtualRoomFilter.filters[roomId]

export const selectIsAuthorizedForRoom = (
  state: { virtualRoomFilter: VirtualRoomFilterState },
  roomId: string,
  sessionId: string
): boolean => {
  const filter = state.virtualRoomFilter.filters[roomId]
  if (!filter) return true
  if (!filter.hasPassword && !filter.hasMeetingLock) return true
  return filter.authorizedSessionIds.includes(sessionId)
}

export const selectRoomHasPassword = (
  state: { virtualRoomFilter: VirtualRoomFilterState },
  roomId: string
): boolean => {
  const filter = state.virtualRoomFilter.filters[roomId]
  return !!(filter?.hasPassword)
}

export const selectRoomOwner = (
  state: { virtualRoomFilter: VirtualRoomFilterState },
  roomId: string
): string | undefined => {
  return state.virtualRoomFilter.filters[roomId]?.ownerSessionId
}

export const selectLastFilterError = (state: { virtualRoomFilter: VirtualRoomFilterState }) =>
  state.virtualRoomFilter.lastError

export const selectCurrentVirtualRoom = (state: { virtualRoomFilter: VirtualRoomFilterState }) =>
  state.virtualRoomFilter.currentVirtualRoom

export const selectKnockRequest = (state: { virtualRoomFilter: VirtualRoomFilterState }) =>
  state.virtualRoomFilter.knockRequest
